import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAudioProcessor } from '../components/AudioRecorder/useAudioProcessor';
import { runWhisper, updateLastRecognitionResult } from '../components/AudioRecorder/useWhisper';
import { compareText } from '../components/AudioRecorder/compareText';
import { handleError } from './errors/errorUtils';
import { useAppConfig } from './AppConfigContext';

  

const AndroidAudioRecorderConfigContext = createContext();
const AndroidAudioRecorderStateContext = createContext();

const isMediaRecorderSupported = () => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm');

export const AndroidAudioRecorderProvider = ({ children, userId, language = 'en', source }) => {
  const [isSupported, setIsSupported] = useState(true);
  const streamRef = useRef(null);

  useEffect(() => {
    setIsSupported(isMediaRecorderSupported());
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return (
    <AndroidAudioRecorderConfigContext.Provider value={{ isSupported, streamRef }}>
      <AndroidAudioRecorderStateProvider userId={userId} language={language} source={source}>
        {children}
      </AndroidAudioRecorderStateProvider>
    </AndroidAudioRecorderConfigContext.Provider>
  );
};

const checkDeviceCompatibility = ({ deviceArch, deviceMemory }) => {
  try {
    // Проверка архитектуры
    const supportedArchs = ['arm64-v8a'];
	console.log(`Ваша архитектура ${deviceArch}, размер памяти ${deviceMemory}`);
    if (!supportedArchs.includes(deviceArch)) {
      return {
        compatible: false,
        reason: 'Unsupported architecture',
        arch: deviceArch
      };
    }

    // Проверка памяти (deviceMemory уже в байтах, конвертируем в MB)
    const totalMemoryMB = deviceMemory / (1024 * 1024);

    if (totalMemoryMB < 4000) {
      return {
        compatible: false,
        reason: 'Insufficient memory',
        totalMemoryMB: Math.round(totalMemoryMB)
      };
    }

    return {
      compatible: true,
      arch: deviceArch,
      totalMemoryMB: Math.round(totalMemoryMB)
    };

  } catch (error) {
    console.error('Device compatibility check failed:', error);
    return {
      compatible: false,
      reason: 'Unable to check device compatibility',
      error: error.message
    };
  }
};


const AndroidAudioRecorderStateProvider = ({ children, userId, language, source }) => {
  const { isSupported, streamRef } = useAndroidAudioRecorderConfig();
  const { deviceArch, deviceMemory, userInfo } = useAppConfig();
  const [isRecording, setIsRecording] = useState(false);
  const [deviceCompatibility, setDeviceCompatibility] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const { processAudioComplete } = useAudioProcessor(0.005, 300, true); //notWavFile = true
  const deviceCompatibilityRef = useRef(null);
  
  // Проверяем совместимость один раз при загрузке компонента
  useEffect(() => {
    const initDeviceCheck = () => {
      const compatibility = checkDeviceCompatibility({ deviceArch, deviceMemory });
      setDeviceCompatibility(compatibility);
      deviceCompatibilityRef.current = compatibility; // Сохраняем в ref
    };

    if (deviceArch && deviceMemory) { // Проверяем, что данные доступны
      initDeviceCheck();
    }
  }, [deviceArch, deviceMemory]);

  const startRecording = useCallback(
    async (onStartRecording, onStopRecording, options = {}) => {
      if (!isSupported) {
        handleError({
          code: 'ERROR_START_RECORDING',
          error: new Error('MediaRecorder not supported'),
          context: { module: 'android_audio_recorder', userId, action: 'start_recording' },
          showToUser: false,
        });
        return;
      }

      // Запрашиваем микрофон только при старте записи
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
      } catch (error) {
        onStopRecording(null, { error: 'Не удалось открыть микрофон' });
        handleError({
          code: 'ERROR_GETTING_STREAM',
          error,
          context: { module: 'android_audio_recorder', userId, action: 'stream_error' },
          showToUser: true,
        });
        return;
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        // Полностью останавливаем треки
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        const completeBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (!completeBlob || completeBlob.size === 0) {
          onStopRecording(null, { error: 'Пустой Blob' });
          handleError({
            code: 'ERROR_EMPTY_BLOB',
            error: new Error('Пустой Blob'),
            context: { module: 'android_audio_recorder', userId, action: 'blob_empty' },
            showToUser: false,
          });
          return;
        }

        // Вызываем runWhisper для транскрипции
        try {
			const { audioData, wavBlob, duration } = await processAudioComplete(completeBlob);
			
			// Проверяем совместимость устройства (уже загружено)
			const currentCompatibility = deviceCompatibilityRef.current;
			  
			if (!currentCompatibility || !currentCompatibility.compatible) {
				console.log(`Device not compatible: ${currentCompatibility?.reason || 'Unknown'}`);
				onStopRecording(wavBlob, { duration, skipWhisper: true });
				return;
			}	
			// Для Whisper используется audioData //'arm64-v8a', 'armeabi-v7a'
			if (source !== 'examScreen' && duration < 5 && userInfo.vip !== true && userInfo.whisper == true) {
				const result = await runWhisper(audioData, duration, language, options);
				const matchedOptionIndex = compareText(result.text, options, language);
				
				// Записываем результат проверки
				await updateLastRecognitionResult(matchedOptionIndex !== null);
				
				if (matchedOptionIndex !== null) {
					onStopRecording(null, { recognizedText: result.text, matchedOptionIndex });
					return;
				}
			}
		  onStopRecording(wavBlob, { duration }); // Если нет совпадения, отправляем wavBlob на сервер 
        } catch (error) {
          handleError({
            code: 'ERROR_WHISPER_TRANSCRIPTION',
            error,
            context: { module: 'android_audio_recorder', userId, action: 'whisper_transcription' },
            showToUser: true,
          });
          onStopRecording(completeBlob, { error: 'Ошибка транскрипции Whisper' });
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      onStartRecording?.();
    },
    [isSupported, userId]
  );

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && mediaRecorderRef.current.state === 'recording') {
      setIsRecording(false);
      mediaRecorderRef.current.stop();
    } else {
      handleError({
        code: 'ERROR_STOP_RECORDING_INVALID_STATE',
        error: new Error('MediaRecorder not in recording state'),
        context: { module: 'android_audio_recorder', state: mediaRecorderRef.current?.state, isRecording, userId, action: 'stop_recording' },
        showToUser: false,
      });
    }
  }, [isRecording, userId]);

  const stateValue = { isRecording, startRecording, stopRecording };

  return (
    <AndroidAudioRecorderStateContext.Provider value={stateValue}>
      {children}
    </AndroidAudioRecorderStateContext.Provider>
  );
};

export const useAndroidAudioRecorderConfig = () => useContext(AndroidAudioRecorderConfigContext);
export const useAndroidAudioRecorderState = () => useContext(AndroidAudioRecorderStateContext);
